import { Inject, Injectable } from '@angular/core';
import { LogService, TOKENS as COMMON_TOKENS } from '@trustedshops/tswp-core-common';
import { Permission, PermissionService, TOKENS as PERMISSIONS_TOKENS } from '@trustedshops/tswp-core-authorization';
import {
  TOKENS as MASTERDATA_TOKENS,
  UserFlowService,
} from '@trustedshops/tswp-core-masterdata';

@Injectable()
export class UserFlowPermissionsService {
  private static readonly TYPE = 'UserFlowPermissionsService';

  constructor(
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(PERMISSIONS_TOKENS.PermissionService)
    private readonly permissionService: PermissionService<Permission>,
    @Inject(MASTERDATA_TOKENS.UserFlowService)
    private readonly userFlowService: UserFlowService,
  ) {}

  /**
   * Updates account-level permissions in UserFlow by syncing local account permissions
   * with the user's profile attributes.
   *
   * This method retrieves the current user's account-level permissions and updates
   * their UserFlow user profile to reflect these permissions. Account permissions
   * are stored directly on the user object rather than in group membership.
   *
   * ⚠️ **WARNING: This method should be called AFTER `userFlow.identify()` has been called**
   * to ensure the user is properly identified in UserFlow before updating their
   * account permissions.
   *
   * @returns A Promise that resolves when the permission update is complete
   *
   * @throws Will log an error if the UserFlow permission update fails, but does not re-throw
   *
   * @see {@link https://docs.userflow.com/docs/userflow-js#updateuser | UserFlow updateUser Documentation}
   * @see {@link https://docs.userflow.com/docs/userflow-js#identify | UserFlow identify Documentation}
   *
   * @example
   * ```typescript
   * // ⚠️ CORRECT: Call userFlow.identify() first, then update account permissions
   * await userFlow.identify('user-123', { name: 'John Doe' });
   * await updateAccountPermissions();
   *
   * // ❌ INCORRECT: Don't call updateAccountPermissions without user identification
   * await updateAccountPermissions(); // May fail or update wrong user
   * ```
   */
  public async updateAccountPermissions(): Promise<void> {
    const accountPermissions = await this.getPermissionsByType('account');

    try {
      await this.userFlowService.userflow.updateUser({ accountPermissions });
    } catch (error) {
      this.logError('Failed to update account permissions in UserFlow', error);
    }
  }

  /**
   * Updates channel-specific permissions in UserFlow by syncing permissions
   * with the user's group membership attributes.
   *
   * This method retrieves the current user's permissions for a specific channel
   * and updates their UserFlow group membership to reflect these permissions.
   * The channel is represented as a UserFlow group, where membership attributes
   * store the user's channel-specific permissions.
   *
   * ⚠️ **WARNING: This method should be called AFTER `userFlow.group()` has been called**
   * to ensure the user is properly associated with the channel group before updating
   * their membership permissions.
   *
   * @param channelId - The unique identifier of the channel to update permissions for
   * @returns A Promise that resolves when the permission update is complete
   *
   * @throws Will log an error if the UserFlow permission update fails, but does not re-throw
   *
   * @see {@link https://docs.userflow.com/docs/userflow-js#groups-companies | UserFlow Groups Documentation}
   * @see {@link https://docs.userflow.com/docs/userflow-js#updategroup | UserFlow updateGroup Documentation}
   *
   * @example
   * ```typescript
   * // Update permissions for a specific channel
   * await updateChannelPermissions('channel-123');
   * ```
   */
  public async updateChannelPermissions(channelId: string): Promise<void> {
    const channelPermissions = await this.getPermissionsByResource(channelId, 'channel');

    try {
      // Use UserFlow's group membership feature to track channel-specific permissions
      // Group represents the channel, membership attributes represent user's permissions in that channel
      // Membership Docs: https://docs.userflow.com/docs/userflow-js#groups-companies
      // Update company Doc: https://docs.userflow.com/docs/userflow-js#updategroup
      await this.userFlowService.userflow.updateGroup({}, {
        membership: { channelPermissions }
      });
    } catch (error) {
      this.logError('Failed to update UserFlow permissions for channel', error);
    }
  }

  /**
   * Retrieves all permission actions for a specific resource type
   * @param resourceType - The type of resource (e.g., 'account', 'channel')
   * @returns Array of permission action strings
   */
  private async getPermissionsByType(resourceType: string): Promise<string[]> {
    try {
      // Fetch user's personal permissions from the permission service
      const permissions = await this.permissionService.getPersonalPermissions();

      // Return empty array if no permissions found
      if (!permissions || !permissions.length) {
        return [];
      }

      // Filter permissions by resource type and flatten all actions into a single array
      return permissions
        .filter(permission => permission.resourceType === resourceType)
        .flatMap(permission => permission.actions);
    } catch (error) {
      this.logError('Failed to get permissions', error);
      return [];
    }
  }

  /**
   * Retrieves all permission actions for a specific resource ID and type
   * @param resourceId - The ID of the specific resource (e.g., channel ID)
   * @param resourceType - The type of resource (e.g., 'channel' or 'account')
   * @returns Array of permission action strings
   */
  private async getPermissionsByResource(resourceId: string, resourceType: string): Promise<string[]> {
    try {
      // Fetch user's personal permissions from the permission service
      const permissions = await this.permissionService.getPersonalPermissions();

      // Return empty array if no permissions found
      if (!permissions || !permissions.length) {
        return [];
      }

      // Filter permissions by both resource ID and type, then flatten all actions
      return permissions
        .filter(permission => permission.resource === resourceId && permission.resourceType === resourceType)
        .flatMap(permission => permission.actions);
    } catch (error) {
      this.logError('Failed to get permissions', error);
      return [];
    }
  }

  /**
   * Logs an error with consistent formatting
   * @param message - Description of the error context
   * @param error - The error object or unknown error
   */
  private logError(message: string, error: unknown): void {
    this.logService.error(
      UserFlowPermissionsService.TYPE,
      new Error(`${message}: ${JSON.stringify(error)}`)
    );
  }
}
